/**
 * PortalSwitcher — iter177.
 *
 * Header pill that lets admin accounts jump between the Admin portal and the
 * User portal of the SAME account (not impersonation — the admin's own user
 * account). Renders NOTHING for non-admins so subscribers never see it.
 *
 * Behaviour:
 *   - Two segments (Admin / User); the current one is highlighted.
 *   - Clicking the other side navigates to the portal's home:
 *       Admin  → `/admin`
 *       User   → `/dashboard/command-center`
 *   - Landing pages are intentionally NOT the last-visited page — a stable
 *     home makes context-switching predictable (locked user pick 2a).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, User as UserIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const ADMIN_HOME = '/admin';
const USER_HOME = '/dashboard/command-center';

const PortalSwitcher = ({ side }) => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Non-admins never see this control.
  if (!isAdmin) return null;

  const isAdminActive = side === 'admin';

  const segmentBase =
    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400';
  const activeCls =
    'bg-[#DC2626] text-white shadow-sm cursor-default';
  const inactiveCls =
    'text-slate-300 hover:text-white hover:bg-white/5 cursor-pointer';

  return (
    <div
      className="hidden md:inline-flex items-center gap-0.5 bg-black/20 border border-white/10 rounded-lg p-0.5"
      role="tablist"
      aria-label="Portal switcher"
      data-testid="portal-switcher"
    >
      <button
        type="button"
        role="tab"
        aria-selected={isAdminActive}
        className={`${segmentBase} ${isAdminActive ? activeCls : inactiveCls}`}
        onClick={() => {
          if (!isAdminActive) navigate(ADMIN_HOME);
        }}
        data-testid="portal-switch-admin"
      >
        <Shield className="h-3.5 w-3.5" />
        Admin
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={!isAdminActive}
        className={`${segmentBase} ${!isAdminActive ? activeCls : inactiveCls}`}
        onClick={() => {
          if (isAdminActive) navigate(USER_HOME);
        }}
        data-testid="portal-switch-user"
      >
        <UserIcon className="h-3.5 w-3.5" />
        User
      </button>
    </div>
  );
};

export default PortalSwitcher;
